import React from 'react';
import StartCard from '../games/StartCard';
import { getGameTypeColor } from '../games/PublicGameCreator';
import { getStylePresets } from './stylePresets';
import { SparklesIcon } from '@heroicons/react/24/outline';
import WebFont from 'webfontloader';
import CustomStyleCreator from './CustomStyleCreator';
import customStylesService from '../services/customStylesService';
import { useUser } from '../userContext';

// Move hexToRgb outside component and add error handling
const hexToRgb = (hex) => {
  try {
    if (!hex || typeof hex !== 'string') return '0, 0, 0';
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return isNaN(r) || isNaN(g) || isNaN(b) ? '0, 0, 0' : `${r}, ${g}, ${b}`;
  } catch (error) {
    console.error('Error in hexToRgb:', error);
    return '0, 0, 0';
  }
};

const DEFAULT_THEME = {
  colors: {
    primary: '#7C3AED',
    accent: '#4B5563',
  },
  typography: {
    primaryFont: 'Inter',
    secondaryFont: 'Inter'
  }
};

const QuickStyleSelector = ({ 
  selectedStyle, 
  onStyleSelect, 
  businessProfile: propBusinessProfile, 
  formData,
  title,
  description,
  helpText,
  isPublicGame = false,
  onStylePresetsUpdate, // New prop to pass combined presets back to parent
  onValidationError // Callback to report validation errors
}) => {
  const { businessProfile } = useUser();
   

    // Add state to track font loading
      const [fontsLoaded, setFontsLoaded] = React.useState(false);
  const [showCustomCreator, setShowCustomCreator] = React.useState(false);
  const [customThemePresets, setCustomThemePresets] = React.useState({});
  const [isLoadingCustomStyles, setIsLoadingCustomStyles] = React.useState(false);
  const [editingStyle, setEditingStyle] = React.useState(null); // { styleId, colors, font }
  const [localSelectionOverride, setLocalSelectionOverride] = React.useState(null); // Override selection when creating new
  const [showValidationError, setShowValidationError] = React.useState(false);

    // Get theme with fallbacks
    const theme = businessProfile?.theme || DEFAULT_THEME;

    let gameTypeColor;
    try {
      if (formData?.gameType?.id) {
        gameTypeColor = getGameTypeColor(formData.gameType.id);
      } else {
        gameTypeColor = theme.colors.primary;
      }
    } catch (error) {
      console.error('Error getting gameTypeColor:', error);
      gameTypeColor = theme.colors.primary;
    }

    const isMapGame = ['pindrop', 'mapselect'].includes(formData?.gameType?.id);
  
    // Load custom styles from database on component mount
    React.useEffect(() => {
      const loadCustomStyles = async () => {
        if (!businessProfile?.id) return;
        
        try {
          setIsLoadingCustomStyles(true);
          const customStyles = await customStylesService.getCompanyCustomStyles(businessProfile.id);
          
          // Convert custom styles to the format expected by stylePresets
          const formattedCustomStyles = {};
          Object.entries(customStyles).forEach(([styleId, styleData]) => {
            formattedCustomStyles[`custom_${styleId}`] = styleData.theme;
          });
          
          setCustomThemePresets(formattedCustomStyles);
        } catch (error) {
          console.error('Error loading custom styles:', error);
        } finally {
          setIsLoadingCustomStyles(false);
        }
      };

      loadCustomStyles();
    }, [businessProfile?.id]);

    const stylePresets = React.useMemo(() => {
      try {
        const basePresets = getStylePresets(gameTypeColor, propBusinessProfile, formData?.gameType?.id);
        // Merge base presets with custom presets
        const combinedPresets = { ...basePresets, ...customThemePresets };
        
        // Notify parent about the updated presets
        if (onStylePresetsUpdate) {
          onStylePresetsUpdate(combinedPresets);
        }
        
        return combinedPresets;
      } catch (error) {
        console.error('Error getting style presets:', error);
        const fallbackPresets = {
          default: {
            colors: {
              background: { gradient: { start: '#F3F4F6', end: '#E5E7EB' } },
              cardBackground: '#FFFFFF',
              cardOpacity: 0.9,
              titleText: '#1F2937',
              subtitleText: '#4B5563',
              buttonBackground: '#4F46E5',
              buttonOpacity: 1,
              buttonText: '#FFFFFF'
            },
            typography: {
              primaryFont: 'Roboto',
              fontWeight: '600'
            }
          }
        };
        
        if (onStylePresetsUpdate) {
          onStylePresetsUpdate(fallbackPresets);
        }
        
        return fallbackPresets;
      }
    }, [gameTypeColor, propBusinessProfile, formData?.gameType?.id, customThemePresets, onStylePresetsUpdate]);

    // Load fonts for all style presets
    React.useEffect(() => {
      const fontsToLoad = Object.values(stylePresets)
        .map(style => [
          style?.typography?.primaryFont,
          style?.typography?.secondaryFont,
          style?.typography?.titleFont,
          style?.typography?.subtitleFont,
          style?.typography?.buttonFont
        ])
        .flat()
        .filter(font => font && font !== 'Inter');

  

      if (fontsToLoad.length > 0) {
        WebFont.load({
          google: {
            families: [...new Set(fontsToLoad)].map(font => `${font}:400,500,600,700`)
          },
          active: () => {
      
            // Force re-render when fonts are loaded
            setFontsLoaded(prev => !prev);
          },
          inactive: () => {
            console.log('Font loading failed'); // Debug log
          }
        });
      }
    }, [stylePresets]);

    // Ensure we have a valid selected style
    let validStyle = selectedStyle;
    if (isPublicGame) {
      // For public games, ensure selected style is one of the allowed ones
      const allowedStyles = ['retro', 'arcade', 'pastel', 'neon'];
      if (!allowedStyles.includes(selectedStyle)) {
        validStyle = 'retro'; // Default to 'retro' for public games
      }
    } else {
      validStyle = stylePresets[selectedStyle] ? selectedStyle : 'default';
    }

    // Auto-correct the selected style if needed and default to first custom style
    React.useEffect(() => {
      if (isPublicGame) {
        const allowedStyles = ['retro', 'arcade', 'pastel', 'neon'];
        if (!allowedStyles.includes(selectedStyle)) {
          onStyleSelect('retro');
        }
      } else {
        // For non-public games, default to first custom style if available
        const customStyleKeys = Object.keys(customThemePresets);
        if (customStyleKeys.length > 0 && !selectedStyle) {
          onStyleSelect(customStyleKeys[0]);
        } else if (customStyleKeys.length > 0 && selectedStyle === 'default') {
          // If currently on default and custom styles are available, switch to first custom
          onStyleSelect(customStyleKeys[0]);
        }
      }
    }, [isPublicGame, selectedStyle, onStyleSelect, customThemePresets]);

    const getBackgroundStyle = (style, theme) => {
      if (isMapGame) {
        return {
          background: 'linear-gradient(to bottom, #F3F4F6, #E5E7EB)',
          backgroundImage: `url(/GameTypeImages/mapbg.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        };
      }
      
      const colors = theme?.colors || {};
      const gradientStart = colors?.background?.gradient?.start || '#F3F4F6';
      const gradientEnd = colors?.background?.gradient?.end || '#E5E7EB';
      
      return {
        background: `linear-gradient(to bottom, ${gradientStart}, ${gradientEnd})`
      };
    };

    const renderStylePreview = (style, colors) => {
      const backgroundStyle = getBackgroundStyle(style, { colors });
      const theme = stylePresets[style];
      const styleColors = theme?.colors || colors;
      const isCustomStyle = style.startsWith('custom_');
      
      // Use local override if set, otherwise use selectedStyle from parent
      const effectiveSelectedStyle = localSelectionOverride !== null ? localSelectionOverride : selectedStyle;
      const isSelected = effectiveSelectedStyle === style;
      


      return (
        <div 
          key={style}
          className={`relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 group
            ${isSelected ? 'ring-2 ring-indigo-500 scale-[1.02]' : 'hover:scale-[1.02]'}`}
          onClick={() => {
                          // Handle custom style selection logic
              if (isCustomStyle) {
                if (selectedStyle === style && showCustomCreator && editingStyle?.styleId === style) {
                  // Clicking on the same custom style that's currently being edited - close creator
                  setShowCustomCreator(false);
                  setEditingStyle(null);
                  setLocalSelectionOverride(null); // Clear override
                } else if (selectedStyle === style && !showCustomCreator) {
                  // Clicking on selected custom style when creator is closed - open for editing
                  handleEditCustomStyle(style);
                  setLocalSelectionOverride(null); // Clear override
                } else {
                  // Clicking on different custom style - select it and close creator
                  onStyleSelect(style);
                  setShowCustomCreator(false);
                  setEditingStyle(null);
                  setLocalSelectionOverride(null); // Clear override
                }
              } else {
                // Regular preset style selection
                onStyleSelect(style);
                setLocalSelectionOverride(null); // Clear override
                if (showCustomCreator) {
                  setShowCustomCreator(false);
                  setEditingStyle(null);
                }
              }
          }}
        >
          <div className="aspect-[3/4] relative" style={backgroundStyle}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div 
                className="w-[85%] max-w-[200px] rounded shadow-xl transform transition-transform duration-200" 
                style={{ 
                  backgroundColor: styleColors.cardBackground ? 
                    `rgba(${hexToRgb(styleColors.cardBackground)}, ${styleColors.cardOpacity || 1})` : 
                    'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }}
              >
                <div className="relative p-3">
                  <div className="max-w-[200px]">
                    <h3 
                      style={{ 
                        color: styleColors.titleText,
                        fontFamily: theme?.typography?.primaryFont || 'Inter',
                        fontWeight: style === 'retro' || style === 'neon' || style === 'arcade' ? 'bold' : 
                                   style === 'pastel' ? 'normal' : '600'
                      }} 
                      className={`text-[10px] sm:text-xs tracking-tight truncate ${style === 'pastel' ? '' : 'font-semibold'}`}
                    >
                      {title || 'Example Title'}
                    </h3>
                    <p 
                      style={{ 
                        color: styleColors.subtitleText,
                        fontFamily: theme?.typography?.primaryFont || 'Inter',
                        fontWeight: style === 'retro' || style === 'neon' || style === 'arcade' ? '500' : 
                                   style === 'pastel' ? 'normal' : '400'
                      }} 
                      className={`text-[8px] sm:text-[10px] mt-0.5 opacity-90 truncate ${style === 'pastel' ? '' : 'font-medium'}`}
                    >
                      {description || 'Example description text'}
                    </p>
                    <button
                      className={`w-full mt-1.5 sm:mt-2 px-2 py-1 rounded text-[8px] sm:text-[10px] shadow-sm transition-all duration-200 ${style === 'pastel' ? '' : 'font-semibold'}`}
                      style={{ 
                        backgroundColor: styleColors.buttonBackground,
                        color: styleColors.buttonText || '#FFFFFF',
                        opacity: styleColors.buttonOpacity || 1,
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        fontFamily: theme?.typography?.primaryFont || 'Inter',
                        fontWeight: style === 'retro' || style === 'neon' ? 'bold' : 
                                   style === 'arcade' ? 'normal' : 
                                   style === 'pastel' ? 'normal' : '600',
                        border: styleColors.buttonBorder ? `1px solid ${styleColors.buttonBorder}` : 'none'
                      }}
                    >
                      Start
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-2 left-2">
            <span className="inline-flex items-center rounded-md bg-black/40 px-2 py-1">
              <p 
                className={`text-white text-xs sm:text-sm capitalize ${style === 'pastel' ? '' : 'font-medium'}`}
                style={{
                  fontFamily: style === 'neon' || style === 'retro' || style === 'arcade' || style === 'pastel' ? 
                    (theme?.typography?.primaryFont || 'Inter') : 'Inter',
                  fontWeight: style === 'neon' || style === 'retro' ? 'bold' : 
                             style === 'arcade' ? 'normal' : 
                             style === 'pastel' ? 'normal' : '500'
                }}
              >
                {style === 'default' ? 'white label' : 
                 style.startsWith('custom_') ? 'custom' : 
                 style}
              </p>
            </span>
          </div>

          {/* Custom Style Management Buttons */}
          {isCustomStyle && !isPublicGame && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCustomStyle(style);
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all duration-200 hover:scale-110"
                style={{
                  backgroundColor: 'rgba(241, 196, 15, 0.9)',
                  color: '#2C3E50',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontFamily: '"Share Tech Mono", monospace',
                  fontWeight: 'bold'
                }}
                title="Edit custom style"
              >
                ✎
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('Are you sure you want to delete this custom style?')) {
                    handleDeleteCustomStyle(style);
                  }
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all duration-200 hover:scale-110"
                style={{
                  backgroundColor: 'rgba(231, 76, 60, 0.9)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontFamily: '"Share Tech Mono", monospace',
                  fontWeight: 'bold'
                }}
                title="Delete custom style"
              >
                ×
              </button>
            </div>
          )}
        </div>
      );
    };

    const handleCustomStyleSave = async (saveData) => {
      if (!businessProfile?.id) {
        console.error('No company ID available for saving custom style');
        return;
      }

      const { theme: customTheme, colors, font, editingStyleId } = saveData;

      try {
        if (editingStyleId) {
          // Update existing style
          const styleData = {
            name: `Custom Style ${new Date().toLocaleDateString()}`,
            colors: colors,
            font: font,
            gameType: formData?.gameType?.id || null,
            theme: customTheme
          };

          await customStylesService.updateCustomStyle(businessProfile.id, editingStyleId.replace('custom_', ''), styleData);
          const customStyleKey = editingStyleId;
          
          // Update local state
          setCustomThemePresets(prev => ({
            ...prev,
            [customStyleKey]: customTheme
          }));
          
          // Keep the same style selected
          onStyleSelect(customStyleKey);
        } else {
          // Create new style
          const styleData = {
            name: `Custom Style ${new Date().toLocaleDateString()}`,
            colors: colors,
            font: font,
            gameType: formData?.gameType?.id || null,
            theme: customTheme
          };

          const savedStyle = await customStylesService.createCustomStyle(businessProfile.id, styleData);
          const customStyleKey = `custom_${savedStyle.id}`;
          
          // Add to local state
          setCustomThemePresets(prev => ({
            ...prev,
            [customStyleKey]: customTheme
          }));
          
          // Select the new custom style
          onStyleSelect(customStyleKey);
        }
        
        // Reset editing state and hide creator
        setEditingStyle(null);
        setShowCustomCreator(false);
      } catch (error) {
        console.error('Error saving custom style:', error);
        // Still add to local state as fallback
        const customStyleKey = editingStyleId || `custom_${Date.now()}`;
        setCustomThemePresets(prev => ({
          ...prev,
          [customStyleKey]: customTheme
        }));
        onStyleSelect(customStyleKey);
        setEditingStyle(null);
        setShowCustomCreator(false);
      }
    };

    const handleDeleteCustomStyle = async (styleKey) => {
      if (!businessProfile?.id) {
        console.error('No company ID available for deleting custom style');
        return;
      }

      const styleId = styleKey.replace('custom_', '');
      
      try {
        await customStylesService.deleteCustomStyle(businessProfile.id, styleId);
        
        // Remove from local state
        setCustomThemePresets(prev => {
          const newPresets = { ...prev };
          delete newPresets[styleKey];
          return newPresets;
        });
        
        // If the deleted style was selected, switch to default
        if (selectedStyle === styleKey) {
          onStyleSelect('default');
        }
      } catch (error) {
        console.error('Error deleting custom style:', error);
        alert('Failed to delete custom style. Please try again.');
      }
    };

    const handleEditCustomStyle = (styleKey) => {
      const theme = customThemePresets[styleKey];
      if (!theme) return;

      // Extract colors back from the theme for editing
      // Look at the generated theme structure to extract original values
      const colors = {
        background: theme.colors?.background?.gradient?.start || '#2C3E50',
        surface: theme.colors?.cardBackground || '#34495E', 
        primary: theme.colors?.titleText || '#F1C40F',
        action: theme.colors?.buttonBackground || '#E74C3C',
        text: theme.colors?.subtitleText || '#ECF0F1'
      };

      const font = theme.typography?.primaryFont || 'Share Tech Mono';

      // Set editing state and select the style being edited
      setEditingStyle({ styleId: styleKey, colors, font });
      setShowCustomCreator(true);
      onStyleSelect(styleKey); // Select the style being edited
    };

    const renderCreateYourOwnCard = () => {
      const isHighlighted = showCustomCreator && !editingStyle; // Only highlight when creating new, not editing
      
      return (
        <div 
          key="create-your-own"
          className={`relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 border-2
            ${isHighlighted ? 'ring-2 ring-yellow-400 scale-[1.02]' : 'hover:scale-[1.02]'}`}
          style={{
            borderColor: isHighlighted ? '#F1C40F' : '#2C3E50',
            backgroundColor: '#34495E'
          }}
          onClick={() => {
            // Toggle the creator for new style creation
            if (showCustomCreator && !editingStyle) {
              // If creator is open for new creation, close it
              setShowCustomCreator(false);
              setEditingStyle(null);
              onStyleSelect(null); // Try null instead of empty string
            } else {
              // Open creator for new creation - WIPE ALL VARIABLES
              setEditingStyle(null); // Clear any editing data
              setShowCustomCreator(true); // Open creator
              setLocalSelectionOverride('__NONE__'); // Use local override to clear visual selection
              onStyleSelect(null); // Still try to notify parent, but don't rely on it
            }
          }}
        >
          <div className="aspect-[3/4] relative" style={{
            background: 'linear-gradient(135deg, #2C3E50, #34495E)'
          }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <SparklesIcon className="w-8 h-8 mb-2" style={{ color: '#F1C40F' }} />
              <p className="text-xs font-bold text-center px-2" style={{
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace',
                textShadow: '0 0 8px rgba(241, 196, 15, 0.3)'
              }}>
                CREATE YOUR OWN
              </p>
              <p className="text-xs font-bold text-center px-2 mt-1" style={{
                color: '#ECF0F1',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                Full customization
              </p>
            </div>
          </div>

          <div className="absolute bottom-2 left-2">
            <span className="inline-flex items-center rounded-md px-2 py-1" style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              border: '1px solid rgba(241, 196, 15, 0.4)'
            }}>
              <p className="text-xs sm:text-sm font-bold" style={{
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                CUSTOM
              </p>
            </span>
          </div>
        </div>
      );
    };


    
    const filteredStyles = Object.entries(stylePresets)
      .filter(([style]) => {
        // For public games, only show retro, arcade, pastel, and neon styles (retro first)
        if (isPublicGame) {
          return ['retro', 'arcade', 'pastel', 'neon'].includes(style);
        }
        // For campaign games, show all styles except monochrome, and include custom styles
        return style !== 'monochrome';
      })
      .sort(([a], [b]) => {
        // For public games, sort by the new order: retro, arcade, pastel, neon
        if (isPublicGame) {
          const order = ['retro', 'arcade', 'pastel', 'neon'];
          return order.indexOf(a) - order.indexOf(b);
        }
        // For studio games, put retro first, then others
        const studioOrder = ['retro', 'default', 'transparent', 'dark', 'arcade', 'pastel', 'neon'];
        const aIndex = studioOrder.indexOf(a);
        const bIndex = studioOrder.indexOf(b);
        
        // If both items are in the order array, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only one item is in the order array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        // If neither is in the order array, maintain original order
        return 0;
      });


    // Separate preset styles from custom styles
    const presetStyles = filteredStyles.filter(([style]) => !style.startsWith('custom_'));
    const customStyles = filteredStyles.filter(([style]) => style.startsWith('custom_'));

    // Check if a valid style is selected for validation
    const hasValidStyleSelected = () => {
      // If we're using local override and it's not a real style, that's invalid
      if (localSelectionOverride === '__NONE__') {
        return false;
      }
      
      // If no style is selected at all
      if (!selectedStyle || selectedStyle === '' || selectedStyle === null) {
        return false;
      }
      
      // If selected style doesn't exist in available styles
      if (!stylePresets[selectedStyle]) {
        return false;
      }
      
      return true;
    };

    // Trigger validation error display when onValidationError prop is called
    React.useEffect(() => {
      if (onValidationError && typeof onValidationError !== 'function') {
        // If onValidationError is true (meaning validation was requested)
        if (!hasValidStyleSelected()) {
          setShowValidationError(true);
          // Auto-hide after 5 seconds
          setTimeout(() => setShowValidationError(false), 5000);
        } else {
          setShowValidationError(false);
        }
      }
    }, [onValidationError, selectedStyle, localSelectionOverride, stylePresets]);

    return (
      <>
        {/* Validation Error Display */}
        {showValidationError && (
          <div className="mb-4 p-4 rounded-lg border-2" style={{
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            borderColor: '#E74C3C',
            color: '#E74C3C'
          }}>
            <div className="flex items-center gap-2">
              <span className="text-lg">🎨</span>
              <div className="font-bold text-sm" style={{
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                Please select a style for your game before creating it!
              </div>
            </div>
          </div>
        )}

        {/* Preset Styles Section - Hidden for public games */}
        {!isPublicGame && (
          <div>
            {customStyles.length > 0 ? (
              // Collapsible preset styles when user has custom styles
              <details className="group">
                <summary className="cursor-pointer list-none">
                  <div className="flex items-center justify-between p-3 rounded-lg transition-colors" style={{
                    backgroundColor: '#34495E',
                    border: '1px solid rgba(241, 196, 15, 0.3)'
                  }}>
                    <h4 className="text-sm font-bold" style={{ 
                      color: '#F1C40F',
                      fontFamily: '"Share Tech Mono", monospace'
                    }}>Preset Styles</h4>
                    <svg 
                      className="w-4 h-4 transition-transform group-open:rotate-180" 
                      fill="none" 
                      stroke="#F1C40F" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="mt-3">
                  <div className={`grid gap-2 sm:gap-4 ${isPublicGame ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'}`}>
                    {presetStyles.map(([style, preset]) => {
                        return renderStylePreview(style, preset.colors);
                      })}
                  </div>
                </div>
              </details>
            ) : (
              // Regular preset styles display when no custom styles
              <>
                <h4 className="text-lg font-bold mb-3" style={{ 
                  color: '#F1C40F',
                  fontFamily: '"Share Tech Mono", monospace',
                  textShadow: '1px 1px 0px #E67E22'
                }}>PRESET STYLES</h4>
                <div className={`grid gap-2 sm:gap-4 ${isPublicGame ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'}`}>
                  {presetStyles.map(([style, preset]) => {
                      return renderStylePreview(style, preset.colors);
                    })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Public Game Styles - Simple grid without header */}
        {isPublicGame && (
          <div className={`grid gap-2 sm:gap-4 ${isPublicGame ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'}`}>
            {presetStyles.map(([style, preset]) => {
                return renderStylePreview(style, preset.colors);
              })}
          </div>
        )}

        {/* Custom Styles Section - Always show for non-public games */}
        {!isPublicGame && (
          <div className="mt-6">
            <h4 className="text-lg font-bold mb-3" style={{ 
              color: '#F1C40F',
              fontFamily: '"Share Tech Mono", monospace',
              textShadow: '1px 1px 0px #E67E22'
            }}>YOUR CUSTOM STYLES</h4>
            <div className={`grid gap-2 sm:gap-4 ${isPublicGame ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'}`}>
              {customStyles.map(([style, preset]) => {
                  return renderStylePreview(style, preset.colors);
                })}
              
              {/* Add Create Your Own card at the end of custom styles */}
              {renderCreateYourOwnCard()}
            </div>
          </div>
        )}

        {/* Custom Style Creator Inline */}
        <CustomStyleCreator
          key={editingStyle?.styleId || 'new'} // Force remount when switching between editing/creating
          isExpanded={showCustomCreator}
          onToggle={() => {
            setShowCustomCreator(false);
            setEditingStyle(null); // Reset editing when closing
          }}
          onSave={handleCustomStyleSave}
          gameType={formData?.gameType?.id}
          title={title}
          description={description}
          initialColors={editingStyle?.colors}
          initialFont={editingStyle?.font}
          editingStyleId={editingStyle?.styleId}
        />
      </>
    );
};

export default QuickStyleSelector; 